/**
 * Events Hook - Fetching and managing events data
 * @file src/web/features/events/hooks/useEvents.ts
 */

import { useState, useEffect } from 'react';
import { config } from '../../auth/config';

export interface EventItem {
  id: number;
  eventId: number;
  caption?: string | null;
  fileKey: string;
  fileName: string;
  fileType: 'image' | 'video';
  mimeType: string;
  fileSize: number;
  url: string;
  urlExpiry: string;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface Event {
  id: number;
  title: string;
  description?: string | null;
  eventDate?: string | null;
  location?: string | null;
  coverImageKey?: string | null;
  coverImageUrl?: string | null;
  coverUrlExpiry?: string | null;
  isActive: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
  items?: EventItem[];
  _count?: {
    items: number;
  };
}

export const useEvents = (includeInactive = false) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const endpoint = includeInactive ? '/api/events/admin' : '/api/events';
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };

      if (includeInactive && token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${config.api.baseUrl}${endpoint}`, {
        headers
      });

      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }

      const data = await response.json();
      setEvents(data.events || []);
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const createEvent = async (eventData: Partial<Event>) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const response = await fetch(`${config.api.baseUrl}/api/events`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(eventData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create event');
    }

    const data = await response.json();
    setEvents(prev => [data.event, ...prev]);
    return data.event;
  };

  const updateEvent = async (id: number, eventData: Partial<Event>) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const response = await fetch(`${config.api.baseUrl}/api/events/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(eventData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update event');
    }

    const data = await response.json();
    setEvents(prev => prev.map(e => e.id === id ? data.event : e));
    return data.event;
  };

  const deleteEvent = async (id: number) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const response = await fetch(`${config.api.baseUrl}/api/events/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete event');
    }

    setEvents(prev => prev.filter(e => e.id !== id));
  };

  const uploadItems = async (eventId: number, files: File[], captions?: string[]) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append('files', file);
      if (captions && captions[index]) {
        formData.append('caption', captions[index]);
      }
    });

    const response = await fetch(`${config.api.baseUrl}/api/events/${eventId}/items`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to upload items');
    }

    return await response.json();
  };

  const deleteItem = async (itemId: number) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const response = await fetch(`${config.api.baseUrl}/api/events/items/${itemId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete item');
    }

    return await response.json();
  };

  useEffect(() => {
    fetchEvents();
  }, [includeInactive]);

  return {
    events,
    loading,
    error,
    refetch: fetchEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    uploadItems,
    deleteItem
  };
};

export const useEvent = (id: number) => {
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEvent = async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${config.api.baseUrl}/api/events/${id}`);

      if (!response.ok) {
        throw new Error('Failed to fetch event');
      }

      const data = await response.json();
      setEvent(data.event);
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching event:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvent();
  }, [id]);

  return {
    event,
    loading,
    error,
    refetch: fetchEvent
  };
};
